/**
 * 是否无意义的空值
 * 例如：null, undefined, NaN, empty string('')
 *
 * @param val - 目标值
 */
export const isEmpty = (val: unknown): boolean =>
  !(val || val === false || val === 0);

export function filterEmptyParams(object: any) {
  Object.keys(object).forEach((key) => {
    if (isEmpty(object[key])) {
      // eslint-disable-next-line no-param-reassign
      delete object[key];
    }
  });
  return object;
}

// 逐位 与 运算
export function and(a = '', b = '') {
  const aArray = a.split('').map((item) => Number(item));
  const bArray = b.split('').map((item) => Number(item));

  const l = Math.max(aArray.length, bArray.length);
  for (let i = l - 1; i >= 0; i -= 1) {
    const aI = aArray[i] || 0;
    const bI = bArray[i] || 0;
    // eslint-disable-next-line no-bitwise
    if (aI & bI) {
      return true;
    }
  }
  return false;
}

export const getBikeInfoFromUrl = (
  // eslint-disable-next-line
  url: any,
): {
  bikeType?: number;
  optMode?: number;
  bikeNo?: string;
  isStake?: boolean;
} => {
  // 注意：f, l已经被供给管理占用
  // 普通车
  const matchsBike = url && url.match(/\?n=[0-9]+/);
  if (/c3x.me/.test(url) && matchsBike) {
    return { bikeType: 1, bikeNo: matchsBike[0].replace('?n=', '') };
  }

  // 校园车
  const matchsSchoolBike = url && url.match(/\?u=[0-9]+/);
  if (/c3x.me/.test(url) && matchsSchoolBike) {
    return { bikeType: 2, bikeNo: matchsSchoolBike[0].replace('?u=', '') };
  }

  // 共享车
  const matchsEBike = url && url.match(/\?e=[0-9]+/);
  if (/c3x.me/.test(url) && matchsEBike) {
    return {
      bikeType: 2,
      optMode: 1,
      bikeNo: matchsEBike[0].replace('?e=', ''),
    };
  }

  // 景区车
  const matchsScenic = url && url.match(/\?m=[0-9]+/);
  if (/c3x.me/.test(url) && matchsScenic) {
    return { bikeType: 2, bikeNo: matchsScenic[0].replace('?m=', '') };
  }

  // 景区助力车
  const matchsScenicEbike = url && url.match(/\?me=[0-9]+/);
  if (/c3x.me/.test(url) && matchsScenicEbike) {
    return { bikeType: 2, bikeNo: matchsScenicEbike[0].replace('?me=', '') };
  }

  // 租售电单车
  const matchsX = url && url.match(/\?x=[0-9]+/);
  if (/c3x.me/.test(url) && matchsX) {
    return { bikeType: 3, bikeNo: matchsX[0].replace('?x=', '') };
  }

  // 有桩车-车
  const matchsStakeBike = url && url.match(/\?c=[0-9]+/);
  if (/c3x.me/.test(url) && matchsStakeBike) {
    return {
      bikeType: 2,
      optMode: 2,
      bikeNo: matchsStakeBike[0].replace('?c=', ''),
    };
  }

  // 有桩车-桩
  const matchsStake = url && url.match(/\?s=[0-9]+/);
  if (/c3x.me/.test(url) && matchsStake) {
    return {
      bikeType: 2,
      optMode: 2,
      isStake: true,
      bikeNo: matchsStake[0].replace('?s=', ''),
    };
  }
  return {};
};

export function getParams(url: string) {
  const params: any = {};
  const search = url.split('?')[1];
  if (search) {
    const parts = search.split('&');
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < parts.length; i++) {
      const nv = parts[i].split('=');
      if (nv[0]) {
        params[nv[0]] = decodeURIComponent(nv[1]) || undefined;
      }
    }
  }
  return params;
}

export function addParams(url: string, params: any) {
  let newUrl = url || '';
  Object.keys(params).forEach((key) => {
    if (params[key] === undefined || params[key] === null) {
      return;
    }
    // 已经有query并且属于hash中的, 或者仅有query
    if (newUrl.indexOf('?') > newUrl.indexOf('#')) {
      newUrl = `${newUrl}&${key}=${encodeURIComponent(params[key])}`;
    } else {
      newUrl = `${newUrl}?${key}=${encodeURIComponent(params[key])}`;
    }
  });
  return newUrl;
}

/**
 *
 * @param {Function}   实际要执行的函数
 * @param {Number}  执行间隔，单位是毫秒（ms）
 * @return {Function} 返回一个“节流”函数
 */
export function throttle(
  fn: () => void,
  threshhold = 500,
): (...rest: [any]) => void {
  let last = 0;
  let timer: NodeJS.Timeout;
  // 返回的函数，每过 threshhold 毫秒就执行一次 fn 函数
  return function func(...rest: [any]) {
    // @ts-ignore
    const context = this;
    const args = rest;

    const now = new Date().getTime();

    // 如果距离上次执行 fn 函数的时间小于 threshhold，那么就放弃
    // 执行 fn，并重新计时
    if (last && now < last + threshhold) {
      clearTimeout(timer);

      // 保证在当前时间区间结束后，再执行一次 fn
      timer = setTimeout(() => {
        last = now;
        // @ts-ignore
        fn.apply(context, args);
      }, threshhold);

      // 在时间区间的最开始和到达指定间隔的时候执行一次 fn
    } else {
      last = now;
      // @ts-ignore
      fn.apply(context, args);
    }
  };
}

/**
 * 获取版本号
 */
export function getVersion() {
  let value = null;
  // eslint-disable-next-line
  const reg = /\b\s*app=easybike(?:;\s*version=([\d\.]+))*/gi;
  const ua = window.navigator.userAgent;
  for (let i = reg.exec(ua); i; i = reg.exec(ua)) {
    value = i;
  }
  if (value) {
    return value[1];
  }
  return '0.0.0';
}

/**
 * 比较版本号
 * @param ver1 版本号1
 * @param ver2 版本号2
 * @returns 0: 相等 | 1: 版本号1 > 版本号2 | -1: 版本号1 < 版本号2
 */
export function versionCompare(ver1: string, ver2: string) {
  const v1 = ver1 || '0.0.0';
  const v2 = ver2 || '0.0.0';
  if (v1 === v2) {
    return 0;
  }
  const v1Arr = v1.split('.');
  const v2Arr = v2.split('.');
  const len = Math.max(v1Arr.length, v2Arr.length);
  for (let i = 0; i < len; i++) {
    const v1Item = Math.floor(Number(v1Arr[i]) || 0);
    const v2Item = Math.floor(Number(v2Arr[i]) || 0);
    if (v1Item < v2Item) {
      return -1;
    } else if (v1Item > v2Item) {
      return 1;
    }
  }
  return 0;
}
// 格式化时间 结果形如：2024.09.05 15:43
export function timeFormat(timestamp: number | string): string {
  const timestampValue = Number(timestamp);
  const date = new Date(timestampValue);
  const Y = date.getFullYear();
  const M =
    date.getMonth() + 1 < 10
      ? '0' + (date.getMonth() + 1)
      : date.getMonth() + 1;
  const D = date.getDate() < 10 ? '0' + date.getDate() : date.getDate();
  const h = date.getHours() < 10 ? '0' + date.getHours() : date.getHours();
  const m =
    date.getMinutes() < 10 ? '0' + date.getMinutes() : date.getMinutes();
  return Y + '.' + M + '.' + D + ' ' + h + ':' + m;
}
